import { useEffect, useRef, useState } from "react";
import type { CurrencyInputProps as BaseCurrencyInputProps } from "react-currency-input-field";
import CurrencyInputField from "react-currency-input-field";

import { cn } from "@/lib/utils";

export interface CurrencyInputProps
  extends Omit<BaseCurrencyInputProps, "onValueChange" | "value"> {
  currency?: "ARS" | "USD";
  value?: number | null;
  onValueChange?: (value: number | null) => void;
  error?: string;
  showHint?: boolean;
}

/**
 * Componente de input para moneda que soporta ARS y USD.
 *
 * Usa locale "es-AR" por lo que:
 * - Separador decimal: coma (,)
 * - Separador de miles: punto (.)
 *
 * El componente mantiene un estado interno como string para permitir
 * que el usuario escriba valores intermedios como "4," sin perder la coma.
 * Solo sincroniza valores numéricos válidos al padre.
 */
export function CurrencyInput({
  currency = "ARS",
  className,
  error,
  showHint = true,
  onValueChange,
  value,
  ...props
}: CurrencyInputProps) {
  // Estado interno como string para preservar lo que el usuario escribe
  const [internalValue, setInternalValue] = useState<string | undefined>(() =>
    value != null ? String(value) : undefined,
  );

  // Flag para evitar ciclos de actualización cuando el cambio viene del usuario
  const isUserInput = useRef(false);

  // Sincronizar desde el padre solo si el cambio no vino del usuario
  useEffect(() => {
    if (!isUserInput.current) {
      setInternalValue(value != null ? String(value) : undefined);
    }
    isUserInput.current = false;
  }, [value]);

  const handleValueChange = (
    rawValue: string | undefined,
    _name?: string,
    values?: { float: number | null; formatted: string; value: string },
  ) => {
    isUserInput.current = true;
    setInternalValue(rawValue);

    // Solo notificar al padre cuando tenemos un valor numérico válido o está vacío
    if (!rawValue || rawValue === "") {
      onValueChange?.(null);
    } else if (values?.float != null) {
      onValueChange?.(values.float);
    }
    // Si rawValue tiene contenido pero float es null (ej: "4,"), no notificamos
    // Esto permite que el usuario termine de escribir el decimal
  };

  return (
    <div className="space-y-1.5">
      <CurrencyInputField
        intlConfig={{ locale: "es-AR", currency: currency }}
        value={internalValue}
        onValueChange={handleValueChange}
        decimalsLimit={2}
        allowNegativeValue={false}
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
          "file:border-0 file:bg-transparent file:text-sm file:font-medium",
          "placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus-visible:ring-destructive/20",
          className,
        )}
        {...props}
      />

      {showHint && !error && (
        <p className="text-xs text-muted-foreground">
          Usa coma para decimales (ej: 1.234,56)
        </p>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
